import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStoreState } from "@/hooks/useStoreState";
import RegulatoryNotificationRepository, { type RegulatoryNotificationItem as HeaderNotificationItem } from "@/utils/API/RegulatoryNotificationRepository";


export default /*#__PURE__*/_defineComponent({
  __name: 'HeaderNotifications',
  props: {
    userKey: null
  },
  setup(__props: any) {

const props = __props as {
  userKey: string;
};



const isOpen = ref(false);
const notifications = ref<HeaderNotificationItem[]>([]);
const rootElement = ref<HTMLElement | null>(null);
const loading = ref(false);
const selectedNotificationIds = ref<number[]>([]);
const selectAllCheckbox = ref<HTMLInputElement | null>(null);
const storeState = useStoreState();

const unreadCount = computed(() => notifications.value.filter((item) => !item.isRead).length);
const hasNotifications = computed(() => notifications.value.length > 0);
const selectedCount = computed(() => selectedNotificationIds.value.length);
const allSelected = computed(() => hasNotifications.value && selectedNotificationIds.value.length === notifications.value.length);
const translationOrFallback = (key: string, fallback: string) => {
  const translation = storeState.value?.LocalizationModule?.translations?.get?.(key);
  return !translation || translation === "---" ? fallback : translation;
};
const unreadLabel = computed(() => `${unreadCount.value} ${translationOrFallback("notifications_unread_label", "unread")}`);

async function syncNotifications() {
  if (!props.userKey) {
    notifications.value = [];
    selectedNotificationIds.value = [];
    return;
  }

  loading.value = true;

  try {
    const response = await RegulatoryNotificationRepository.listUnreadNotifications();
    const items = Array.isArray(response.data?.result) ? response.data.result : [];
    notifications.value = items;
    selectedNotificationIds.value = selectedNotificationIds.value.filter((id) => items.some((item) => item.id === id));
  }
  catch {
    notifications.value = [];
    selectedNotificationIds.value = [];
  }
  finally {
    loading.value = false;
  }
}

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

async function markAsRead(notificationId: number) {
  const targetItem = notifications.value.find((item) => item.id === notificationId);
  if (!targetItem || targetItem.isRead) {
    return;
  }

  const previousItems = [...notifications.value];
  notifications.value = notifications.value.map((item) => (
    item.id === notificationId
      ? { ...item, isRead: true }
      : item
  ));

  try {
    await RegulatoryNotificationRepository.dismiss(notificationId);
  }
  catch {
    notifications.value = previousItems;
  }
}

function toggleSelection(notificationId: number, isSelected: boolean) {
  if (isSelected) {
    if (!selectedNotificationIds.value.includes(notificationId)) {
      selectedNotificationIds.value = [...selectedNotificationIds.value, notificationId];
    }
    return;
  }

  selectedNotificationIds.value = selectedNotificationIds.value.filter((id) => id !== notificationId);
}

function toggleSelectAll(isSelected: boolean) {
  selectedNotificationIds.value = isSelected ? notifications.value.map((item) => item.id) : [];
}

function handleSelectionChange(notificationId: number, event: Event) {
  const target = event.target as HTMLInputElement | null;
  toggleSelection(notificationId, target?.checked ?? false);
}

function handleSelectAllChange(event: Event) {
  const target = event.target as HTMLInputElement | null;
  toggleSelectAll(target?.checked ?? false);
}

async function clearSelected() {
  if (!selectedNotificationIds.value.length) {
    return;
  }

  const idsToClear = [...selectedNotificationIds.value];
  const previousItems = [...notifications.value];

  notifications.value = notifications.value.filter((item) => !idsToClear.includes(item.id));
  selectedNotificationIds.value = [];

  try {
    await RegulatoryNotificationRepository.dismissAll(idsToClear);
  }
  catch {
    notifications.value = previousItems;
  }
}

function handleRowClick(item: HeaderNotificationItem) {
  void markAsRead(item.id);
}

function handleLinkClick(item: HeaderNotificationItem) {
  void markAsRead(item.id);
}

function formatTimestamp(value: string) {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function handleDocumentClick(event: MouseEvent) {
  const target = event.target as Node | null;
  if (!rootElement.value || !target) {
    return;
  }

  if (!rootElement.value.contains(target)) {
    isOpen.value = false;
  }
}

function handleOpenNotifications() {
  isOpen.value = true;
}

watch(() => props.userKey, syncNotifications, { immediate: true });
watch([selectedCount, hasNotifications], () => {
  if (!selectAllCheckbox.value) {
    return;
  }

  selectAllCheckbox.value.indeterminate = selectedCount.value > 0 && !allSelected.value;
});

onMounted(() => {
  document.addEventListener("click", handleDocumentClick);
  window.addEventListener("menusano:open-notifications", handleOpenNotifications as EventListener);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
  window.removeEventListener("menusano:open-notifications", handleOpenNotifications as EventListener);
});

return { __sfc: true,props, isOpen, notifications, rootElement, loading, selectedNotificationIds, selectAllCheckbox, storeState, unreadCount, hasNotifications, selectedCount, allSelected, translationOrFallback, unreadLabel, syncNotifications, toggleOpen, markAsRead, toggleSelection, toggleSelectAll, handleSelectionChange, handleSelectAllChange, clearSelected, handleRowClick, handleLinkClick, formatTimestamp, handleDocumentClick, handleOpenNotifications }
}

})